import { BALANCE } from '../balance.ts';
import type { Rng } from '../rng.ts';
import type { PuzzleAct, PuzzleInstance, Role } from '../types.ts';
import {
  failResult,
  okResult,
  type ActContext,
  type GenContext,
  type PuzzleModule,
  type SolveInfo,
  type TamperChoice,
  type ViewContext,
} from './types.ts';
import { VOCAB, type WordCategory } from './vocab.ts';
import { alterableClues, clueByKind, playersWithRole } from './util.ts';

/**
 * Password (GDD §8.6). The hacker guesses one word from a list of candidates; clues about
 * the word are scattered among the other roles. Guesses get letter-by-letter feedback.
 */

export type PasswordClue =
  | { k: 'category'; cat: WordCategory }
  | { k: 'first'; letter: string }
  | { k: 'last'; letter: string }
  | { k: 'contains'; letter: string }
  | { k: 'excludes'; letter: string };

export type Mark = 'hit' | 'present' | 'miss';

export interface PasswordParams {
  length: number;
  /** Candidate words and their categories, in display order. */
  candidates: { w: string; cat: WordCategory }[];
  answer: number;
  maxAttempts: number;
}

export interface PasswordState {
  guesses: { word: number; marks: Mark[] }[];
  attemptsLeft: number;
  lockedUntilMs: number;
}

const CANDIDATES_BY_LEVEL = [6, 6, 8, 10, 12, 16] as const;
const ATTEMPTS_BY_LEVEL = [6, 6, 6, 5, 5, 4] as const;
const CLUES_BY_LEVEL = [4, 4, 4, 3, 3, 3] as const;
const REFILL = 2;
const LOCK_MS = 20_000;

const params = (p: PuzzleInstance) => p.params as PasswordParams;
const state = (p: PuzzleInstance) => p.state as PasswordState;

export function clueHolds(c: PasswordClue, w: string, cat: WordCategory): boolean {
  switch (c.k) {
    case 'category':
      return cat === c.cat;
    case 'first':
      return w.startsWith(c.letter);
    case 'last':
      return w.endsWith(c.letter);
    case 'contains':
      return w.includes(c.letter);
    case 'excludes':
      return !w.includes(c.letter);
  }
}

/** Wordle-style marks with correct handling of repeated letters. */
export function markGuess(answer: string, guess: string): Mark[] {
  const marks: Mark[] = new Array<Mark>(guess.length).fill('miss');
  const left: Record<string, number> = {};
  for (let i = 0; i < answer.length; i++) {
    if (guess[i] === answer[i]) marks[i] = 'hit';
    else left[answer.charAt(i)] = (left[answer.charAt(i)] ?? 0) + 1;
  }
  for (let i = 0; i < guess.length; i++) {
    if (marks[i] === 'hit') continue;
    const ch = guess.charAt(i);
    if ((left[ch] ?? 0) > 0) {
      marks[i] = 'present';
      left[ch] = (left[ch] ?? 0) - 1;
    }
  }
  return marks;
}

function candidateClues(par: PasswordParams): PasswordClue[] {
  const ans = par.candidates[par.answer] as { w: string; cat: WordCategory };
  const out: PasswordClue[] = [
    { k: 'category', cat: ans.cat },
    { k: 'first', letter: ans.w.charAt(0) },
    { k: 'last', letter: ans.w.charAt(ans.w.length - 1) },
  ];
  for (const letter of new Set(ans.w)) out.push({ k: 'contains', letter });
  const others = new Set(par.candidates.flatMap((c) => [...c.w]));
  for (const letter of others) if (!ans.w.includes(letter)) out.push({ k: 'excludes', letter });
  return out;
}

function remainingCandidates(par: PasswordParams, clues: readonly PasswordClue[]): number[] {
  const out: number[] = [];
  par.candidates.forEach((c, i) => {
    if (clues.every((cl) => clueHolds(cl, c.w, c.cat))) out.push(i);
  });
  return out;
}

function generate(ctx: GenContext) {
  const { rng, level } = ctx;
  const length = rng.pick([5, 6] as const);
  const pool = VOCAB[ctx.language][length];
  const count = Math.min(CANDIDATES_BY_LEVEL[level] ?? 8, pool.length);
  const candidates = rng.shuffle(pool).slice(0, count);
  const answer = rng.int(0, count - 1);
  const par: PasswordParams = {
    length,
    candidates,
    answer,
    maxAttempts: ATTEMPTS_BY_LEVEL[level] ?? 5,
  };

  // Greedy: keep adding the true clue that narrows the field most until exactly one word fits.
  const all = rng.shuffle(candidateClues(par));
  const chosen: PasswordClue[] = [];
  let left = remainingCandidates(par, chosen);
  while (left.length > 1 && all.length > 0) {
    let best = 0;
    let bestLeft = Number.POSITIVE_INFINITY;
    all.forEach((c, i) => {
      const n = remainingCandidates(par, [...chosen, c]).length;
      if (n < bestLeft) {
        bestLeft = n;
        best = i;
      }
    });
    chosen.push(all.splice(best, 1)[0] as PasswordClue);
    left = remainingCandidates(par, chosen);
  }
  // Pad with extra true clues up to the level's target so information is spread out.
  const target = CLUES_BY_LEVEL[level] ?? 3;
  while (chosen.length < target && all.length > 0) chosen.push(all.shift() as PasswordClue);

  const holders = ['mastermind', 'locksmith', 'lookout', 'driver'].filter((r) =>
    ctx.roles.includes(r as Role),
  ) as Role[];
  const start = rng.int(0, holders.length - 1);
  const clues = chosen.map((value, i) => ({
    id: `clue${i}`,
    kind: 'passwordClue',
    holder: holders[(start + i) % holders.length] as Role,
    value,
  }));

  const s: PasswordState = { guesses: [], attemptsLeft: par.maxAttempts, lockedUntilMs: 0 };
  return { params: par, state: s, clues, solvers: ['hacker'] as Role[] };
}

function apply(p: PuzzleInstance, act: PuzzleAct, ctx: ActContext) {
  if (act.kind !== 'password.try') return failResult('invalid', 'not a password action');
  const par = params(p);
  const st = state(p);
  if (ctx.actorRole !== 'hacker')
    return failResult('notAllowed', 'only the hacker guesses passwords');
  if (!Number.isInteger(act.word) || act.word < 0 || act.word >= par.candidates.length) {
    return failResult('invalid', 'no such candidate');
  }
  if (ctx.execMs < st.lockedUntilMs) return failResult('cooldown', 'the terminal is locked');
  const audience = playersWithRole(ctx.players, 'hacker');
  const guess = (par.candidates[act.word] as { w: string }).w;
  const answer = (par.candidates[par.answer] as { w: string }).w;
  const marks = markGuess(answer, guess);
  if (act.word === par.answer) {
    st.guesses.push({ word: act.word, marks });
    return okResult({ solved: true, events: [{ type: 'password.accepted', audience, data: {} }] });
  }
  const prevLeft = st.attemptsLeft;
  const prevLock = st.lockedUntilMs;
  st.guesses.push({ word: act.word, marks });
  st.attemptsLeft -= 1;
  if (st.attemptsLeft <= 0) {
    st.lockedUntilMs = ctx.execMs + LOCK_MS;
    st.attemptsLeft = REFILL;
  }
  return okResult({
    errorTenths: BALANCE.penalty.password,
    undo: { kind: 'popGuess', data: { prevLeft, prevLock } },
    events: [{ type: 'password.denied', audience, data: { word: act.word } }],
  });
}

function view(p: PuzzleInstance, ctx: ViewContext): Record<string, unknown> {
  const par = params(p);
  const st = state(p);
  const base: Record<string, unknown> = {
    type: 'password',
    solved: p.solved,
    length: par.length,
    clues: {},
  };
  // Several clues can share a holder, so they are listed rather than keyed by kind.
  base['clueList'] = p.clues
    .filter((c) => c.holder === ctx.role)
    .map((c) => (c.tampered ? c.tampered.value : c.value));
  if (ctx.role === 'hacker') {
    base['candidates'] = par.candidates.map((c) => c.w);
    base['guesses'] = st.guesses.map((g) => ({ word: g.word, marks: g.marks.slice() }));
    base['attemptsLeft'] = st.attemptsLeft;
    base['lockedUntilMs'] = st.lockedUntilMs;
  }
  if (ctx.revealed) base['secret'] = { answer: par.answer };
  return base;
}

function legalActs(p: PuzzleInstance, role: Role): PuzzleAct[] {
  if (role !== 'hacker' || p.solved) return [];
  const tried = new Set(state(p).guesses.map((g) => g.word));
  return params(p)
    .candidates.map((_, word) => word)
    .filter((w) => !tried.has(w))
    .map((word) => ({ kind: 'password.try' as const, word }));
}

function tamper(p: PuzzleInstance, rng: Rng, exclude: Role | null): TamperChoice | null {
  const clues = alterableClues(p, exclude);
  if (clues.length === 0) return null;
  const clue = rng.pick(clues);
  const c = clue.value as PasswordClue;
  const par = params(p);
  const letters = [...new Set(par.candidates.flatMap((x) => [...x.w]))];
  let value: PasswordClue;
  switch (c.k) {
    case 'category': {
      const cats: WordCategory[] = ['place', 'tool', 'person', 'loot', 'vice'];
      value = { k: 'category', cat: rng.pick(cats.filter((x) => x !== c.cat)) };
      break;
    }
    case 'first':
      value = { k: 'first', letter: rng.pick(letters.filter((x) => x !== c.letter)) };
      break;
    case 'last':
      value = { k: 'last', letter: rng.pick(letters.filter((x) => x !== c.letter)) };
      break;
    case 'contains':
      value = { k: 'contains', letter: rng.pick(letters.filter((x) => x !== c.letter)) };
      break;
    case 'excludes':
      value = { k: 'excludes', letter: rng.pick(letters.filter((x) => x !== c.letter)) };
      break;
  }
  void clueByKind;
  return { clueId: clue.id, value };
}

function solve(raw: unknown): SolveInfo {
  const par = raw as PasswordParams;
  return {
    solvable: par.answer >= 0 && par.answer < par.candidates.length,
    difficulty: par.candidates.length,
  };
}

/** Number of candidates that satisfy every clue. A well-formed puzzle has exactly one. */
export function consistentCandidates(p: PuzzleInstance): number[] {
  const clues = p.clues.map((c) => c.value as PasswordClue);
  return remainingCandidates(params(p), clues);
}

function autoSolve(p: PuzzleInstance): void {
  const par = params(p);
  state(p).guesses.push({ word: par.answer, marks: markGuess('', '') });
}

function undo(p: PuzzleInstance, u: { kind: string; data?: unknown }): void {
  if (u.kind !== 'popGuess') return;
  const d = u.data as { prevLeft: number; prevLock: number };
  const st = state(p);
  st.guesses.pop();
  st.attemptsLeft = d.prevLeft;
  st.lockedUntilMs = d.prevLock;
}

export const passwordModule: PuzzleModule = {
  type: 'password',
  generate,
  apply,
  view,
  legalActs,
  tamper,
  autoSolve,
  undo,
  solve,
};
